import React, { useState } from 'react';
import { ChevronDown, ChevronRight, AlertTriangle } from 'lucide-react';
import { ApiEndpoint } from '@/types/api';

interface ApiCardProps {
  api: ApiEndpoint;
}

export const ApiCard: React.FC<ApiCardProps> = ({ api }) => {
  const [isExpanded, setIsExpanded] = useState(false);

  const getMethodColor = (method: string): string => {
    switch (method.toUpperCase()) {
      case 'GET':
        return 'bg-green-100 text-green-800';
      case 'POST':
        return 'bg-blue-100 text-blue-800';
      case 'PUT':
        return 'bg-yellow-100 text-yellow-800';
      case 'DELETE':
        return 'bg-red-100 text-red-800';
      case 'PATCH':
        return 'bg-purple-100 text-purple-800';
      default:
        return 'bg-gray-100 text-gray-800';
    }
  };


  return (
    <div className="bg-white hover:bg-gray-50 transition-colors">
      <div
        className="px-6 py-4 cursor-pointer"
        onClick={() => setIsExpanded(!isExpanded)}
      >
        <div className="flex flex-col space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center space-x-4 flex-1 min-w-0">
              <div className="flex items-center space-x-3 flex-shrink-0">
                {isExpanded ? (
                  <ChevronDown className="w-4 h-4 text-gray-500" />
                ) : (
                  <ChevronRight className="w-4 h-4 text-gray-500" />
                )}
                <span
                  className={`px-2 py-1 rounded text-xs font-semibold ${getMethodColor(
                    api.method
                  )}`}
                >
                  {api.method.toUpperCase()}
                </span>
              </div>
              
              <div className="flex-1 min-w-0">
                <div className="flex items-center space-x-3">
                  <h4 className="text-sm font-medium text-gray-900 break-words">
                    {api.name}
                  </h4>
                  {api.deprecated && (
                    <AlertTriangle className="w-4 h-4 text-amber-500 flex-shrink-0" />
                  )}
                </div>
              </div>
            </div>
            
            <div className="flex items-center space-x-2 flex-shrink-0">
              {api.version && (
                <span className="text-xs bg-gray-200 text-gray-700 px-2 py-1 rounded">
                  v{api.version}
                </span>
              )}
              <div className="w-2 h-2 bg-emerald-400 rounded-full"></div>
            </div>
          </div>
          
          <div className="flex flex-col space-y-2">
            <code className="text-sm font-mono text-gray-700 bg-gray-100 px-2 py-1 rounded break-all">
              {api.path}
            </code>
            {api.description && (
              <p className="text-sm text-gray-600 break-words">{api.description}</p>
            )}
          </div>
        </div>
      </div>

      {isExpanded && (
        <div className="border-t border-gray-200 p-4 bg-gray-50">
          <div className="space-y-4">
            {/* Authentication */}
            {api.authentication && api.authentication.type !== 'none' && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Authentication</h4>
                <div className="bg-white p-3 rounded border">
                  <p className="text-sm">
                    <span className="font-medium">Type:</span> {api.authentication.type}
                  </p>
                  {api.authentication.description && (
                    <p className="text-sm mt-1 text-gray-600">
                      {api.authentication.description}
                    </p>
                  )}
                </div>
              </div>
            )}

            {api.parameters && api.parameters.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Parameters</h4>
                <div className="bg-white rounded border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">In</th>
                        <th className="px-3 py-2 text-left">Required</th>
                        <th className="px-3 py-2 text-left">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {api.parameters.map((param, index) => (
                        <tr key={index} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-mono text-xs">{param.name}</td>
                          <td className="px-3 py-2 text-xs">{param.type}</td>
                          <td className="px-3 py-2 text-xs">{param.in}</td>
                          <td className="px-3 py-2 text-xs">
                            {param.required ? (
                              <span className="text-red-600 font-semibold">Yes</span>
                            ) : (
                              <span className="text-gray-500">No</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {param.description || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Header Parameters */}
            {api.headerParameters && api.headerParameters.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Header Parameters</h4>
                <div className="bg-white rounded border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Required</th>
                        <th className="px-3 py-2 text-left">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {api.headerParameters.map((param, index) => (
                        <tr key={index} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-mono text-xs">{param.name}</td>
                          <td className="px-3 py-2 text-xs">{param.type}</td>
                          <td className="px-3 py-2 text-xs">
                            {param.required ? (
                              <span className="text-red-600 font-semibold">Yes</span>
                            ) : (
                              <span className="text-gray-500">No</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {param.description || '-'}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Request Body Parameters */}
            {api.requestBodyParameters && api.requestBodyParameters.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Request Body Parameters</h4>
                <div className="bg-white rounded border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Name</th>
                        <th className="px-3 py-2 text-left">Type</th>
                        <th className="px-3 py-2 text-left">Required</th>
                        <th className="px-3 py-2 text-left">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {api.requestBodyParameters.map((param, index) => (
                        <tr key={index} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-mono text-xs">{param.name}</td>
                          <td className="px-3 py-2 text-xs">{param.type}</td>
                          <td className="px-3 py-2 text-xs">
                            {param.required ? (
                              <span className="text-red-600 font-semibold">Yes</span>
                            ) : (
                              <span className="text-gray-500">No</span>
                            )}
                          </td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {param.description}
                            {param.default !== undefined && (
                              <span className="text-gray-500 ml-2">(default: {JSON.stringify(param.default)})</span>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Request Example or Request Body */}
            {(api.requestExample || api.requestBody) && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">
                  {api.requestExample ? 'Request Example' : 'Request Body Schema'}
                </h4>
                <pre className="bg-white p-3 rounded border text-xs overflow-x-auto">
                  <code>{JSON.stringify(api.requestExample || api.requestBody, null, 2)}</code>
                </pre>
              </div>
            )}

            {/* Response Example */}
            {api.responseExample && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Response Example</h4>
                <pre className="bg-white p-3 rounded border text-xs overflow-x-auto max-h-96">
                  <code>{JSON.stringify(api.responseExample, null, 2)}</code>
                </pre>
              </div>
            )}

            {/* Status Codes */}
            {api.statusCodes && api.statusCodes.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Status Codes</h4>
                <div className="bg-white rounded border overflow-hidden">
                  <table className="w-full text-sm">
                    <thead className="bg-gray-100">
                      <tr>
                        <th className="px-3 py-2 text-left">Code</th>
                        <th className="px-3 py-2 text-left">Description</th>
                      </tr>
                    </thead>
                    <tbody>
                      {api.statusCodes.map((status, index) => (
                        <tr key={index} className="border-t border-gray-100">
                          <td className="px-3 py-2 font-mono text-xs">{status.code}</td>
                          <td className="px-3 py-2 text-xs text-gray-600">
                            {status.description}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* Tags */}
            {api.tags && api.tags.length > 0 && (
              <div>
                <h4 className="font-semibold text-sm text-gray-700 mb-2">Tags</h4>
                <div className="flex flex-wrap gap-2">
                  {api.tags.map((tag, index) => (
                    <span
                      key={index}
                      className="px-2 py-1 bg-blue-100 text-blue-800 text-xs rounded-full"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};
